/**
 * Thumbnail pool (CB-5a) — the project switcher's cursor-pool loader
 * (project-history-modal.js `loadThumbnails`, POOL = 4) generalized over an
 * arbitrary async resolver. A small pool keeps a large library from opening
 * dozens of parallel requests; the caller owns the resolved URLs and their
 * revocation (`releaseAllThumbnails()` is process-global — see the plan's
 * standing rule — so the MODULE never revokes).
 */

/**
 * DS-3 generalised `apply`. The default is unchanged — set `img.src`, unhide,
 * fire the event — but the decorative library resolves SVG SOURCE TEXT rather
 * than a URL and injects it inline, and the alternative was a second unbounded
 * fetcher beside this one. `apply` is the only difference between the two, so
 * it is the only thing that varies; the pooling, the cancellation and the
 * "never write into a detached node" rule stay in one place.
 *
 * @param {{
 *   resolve: (item: object, ctx: {signal?: AbortSignal}) => Promise<any>,
 *   apply?: (target: HTMLElement, value: any, item: object) => void,
 *   concurrency?: number,
 *   signal?: AbortSignal
 * }} opts
 * @returns {{
 *   enqueue: (item: object, target: HTMLElement) => void,
 *   cancel: () => void,
 *   inFlight: () => number,
 *   maxConcurrent: () => number
 * }}
 */
export function createThumbnailPool({ resolve, apply, concurrency = 4, signal } = {}) {
  if (typeof resolve !== 'function') {
    throw new Error('createThumbnailPool requires resolve()');
  }
  const applyValue =
    typeof apply === 'function'
      ? apply
      : (img, url) => {
          img.src = url;
          img.hidden = false;
          img.dispatchEvent(new CustomEvent('pb-coll-thumb', { bubbles: true }));
        };
  const pending = [];
  let cancelled = false;
  let workers = 0;
  let inFlight = 0;
  // The high-water mark, so a suite can assert the cap was RESPECTED rather
  // than merely configured — `inFlight()` sampled after the fact is always 0.
  let peak = 0;

  // A worker drains the queue and NEVER rejects: the await sits inside the
  // try/catch, and everything outside it is synchronous bookkeeping — which is
  // what lets enqueue() start it as a plain floating promise with no rejection
  // handler to swallow.
  async function worker() {
    while (!cancelled && pending.length) {
      const task = pending.shift();
      inFlight += 1;
      if (inFlight > peak) peak = inFlight;
      try {
        const value = await resolve(task.item, { signal });
        // The surface may have closed, cancelled, or re-rendered while this
        // was in flight — never write into a detached or superseded node.
        if (value && !cancelled && task.img.isConnected) {
          applyValue(task.img, value, task.item);
        }
      } catch {
        // The resolver owns reporting (donor discipline: the loader reports
        // and swallows); a failed thumbnail leaves the placeholder in place.
      } finally {
        inFlight -= 1;
      }
    }
  }

  return {
    enqueue(item, img) {
      if (cancelled || !img) return;
      pending.push({ item, img });
      if (workers < concurrency) {
        workers += 1;
        worker().finally(() => {
          workers -= 1;
        });
      }
    },
    cancel() {
      cancelled = true;
      pending.length = 0;
    },
    inFlight: () => inFlight,
    maxConcurrent: () => peak
  };
}
